'use client'

import { useState, useCallback } from 'react'
import Image from 'next/image'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '~/components/ui/button'

interface FacilitatorImageCarouselProps {
  images: string[]
  alt: string
}

export function FacilitatorImageCarousel({ images, alt }: FacilitatorImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  // If only one image or no images, just show it
  if (!images || images.length <= 1) {
    const imageUrl = images && images.length > 0 ? images[0] : null
    if (!imageUrl) return null
    
    return (
      <div className="relative w-full aspect-[2/1] rounded-xl overflow-hidden shadow-sm border border-border bg-neutral-100">
        {/* Blurred background image */}
        <div className="absolute inset-0">
          <Image 
            src={imageUrl} 
            alt=""
            width={2160}
            height={1080}
            className="w-full h-full object-cover scale-110 blur-2xl opacity-50"
            unoptimized
            aria-hidden="true"
          />
        </div>
        {/* Main image */}
        <div className="relative w-full h-full flex items-center justify-center">
          <Image 
            src={imageUrl} 
            alt={alt}
            width={2160}
            height={1080}
            className="max-w-full max-h-full object-contain"
            unoptimized
          />
        </div>
      </div>
    )
  }

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
  }, [images.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)
  }, [images.length])

  return (
    <div className="relative w-full aspect-[2/1] rounded-xl overflow-hidden shadow-sm border border-border bg-neutral-100 group">
      {images.map((imageUrl, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-500 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Blurred background image */}
          <div className="absolute inset-0">
            <Image 
              src={imageUrl} 
              alt=""
              width={2160}
              height={1080}
              className="w-full h-full object-cover scale-110 blur-2xl opacity-50"
              unoptimized
              aria-hidden="true"
            />
          </div>
          {/* Main image */}
          <div className="relative w-full h-full flex items-center justify-center">
            <Image 
              src={imageUrl} 
              alt={`${alt} - Image ${index + 1}`}
              width={2160}
              height={1080}
              className="max-w-full max-h-full object-contain"
              priority={index === 0}
              unoptimized
            />
          </div>
        </div>
      ))}

      {/* Left Navigation Button */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={goToPrevious}
        aria-label="Previous image"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>

      {/* Right Navigation Button */}
      <Button
        variant="secondary"
        size="icon"
        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={goToNext}
        aria-label="Next image"
      >
        <ChevronRight className="h-5 w-5" />
      </Button>

      {/* Image Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50 hover:bg-white/75'
              }`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

